// WhatsApp outreach templates + deep-link helpers for cross-sell follow-ups.
//
// Usage:
//   import { openWhatsApp, buildCrossSellMessage } from "@/lib/whatsappTemplates";
//   openWhatsApp(phone, buildCrossSellMessage(booking, "boat_charter"));
//
// Templates are service-specific with a generic fallback. `{name}` placeholder is
// replaced with the customer's first name. On mobile devices we use the
// `whatsapp://` scheme so the native app opens directly; on desktop we fall back
// to `https://wa.me/` which loads WhatsApp Web.

const SERVICE_TEMPLATES = {
  boat_charter:
    "Hi {name}! It's Juke Transfers · following up on your interest in a private boat charter (Blue Cave, Hvar, Pakleni Islands…). Would you like me to send over a couple of curated skipper + yacht options for your dates?",
  scooter_quad:
    "Hi {name}! It's Juke Transfers · you mentioned scooter / quad rental during your booking. I can lock in a delivery-to-hotel scooter or a half-day quad adventure — want me to send options + prices?",
  rent_a_car:
    "Hi {name}! It's Juke Transfers · following up on your rent-a-car interest. I can arrange a car delivered to your hotel or the airport, fully insured. Want me to send a quick pick of models for your dates?",
  guided_tours:
    "Hi {name}! It's Juke Transfers · following up on the guided tour you were curious about. I can put together a private English-speaking guide for Split, Trogir, Krka or Plitvice — want a couple of options?",
  wine_tour:
    "Hi {name}! It's Juke Transfers · following up on the private wine tour. I can arrange a full-day cellar tour through Pelješac or Hvar with a dedicated driver + tastings included. Want me to send a shortlist?",
  helicopter:
    "Hi {name}! It's Juke Transfers · following up on your helicopter transfer interest. I can lock in a private lift Split↔Hvar/Dubrovnik with 24h notice. Want me to send availability + a firm quote?",
  private_jet:
    "Hi {name}! It's Juke Transfers · following up on your private jet enquiry. Happy to pull live availability + a firm price for your route. Could you confirm the exact date and passenger count?",
};

const GENERIC_TEMPLATE =
  "Hi {name}! It's Juke Transfers · following up on your interest in {service}. Happy to put together tailored options for your dates — want me to send a shortlist?";

const firstName = (fullName) =>
  (fullName || "").trim().split(/\s+/)[0] || "there";

// Nice human label for the fallback template (falls back to the raw code).
const LABELS = {
  boat_charter: "a private boat charter",
  scooter_quad: "a scooter / quad rental",
  rent_a_car:   "a rent-a-car",
  guided_tours: "a guided tour",
  wine_tour:    "a private wine tour",
  helicopter:   "a helicopter transfer",
  private_jet:  "a private jet charter",
};

export function buildCrossSellMessage(booking, code) {
  const name = firstName(booking?.customer_name || booking?.name);
  const template = SERVICE_TEMPLATES[code] || GENERIC_TEMPLATE;
  const service = LABELS[code] || (code || "our services").replace(/_/g, " ");
  return template.replace(/\{name\}/g, name).replace(/\{service\}/g, service);
}

/**
 * Build a Google Maps search URL for the booking's pickup point.
 * Prefers precise lat/lng if the customer picked a Places pin, falls
 * back to the free-text `pickup_location`.
 */
export function buildPickupMapsUrl(booking) {
  if (!booking) return "";
  const lat = booking.pickup_lat ?? booking?.pickup_coords?.lat;
  const lng = booking.pickup_lng ?? booking?.pickup_coords?.lng;
  if (typeof lat === "number" && typeof lng === "number") {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const q = (booking.pickup_location || "").trim();
  if (!q) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/**
 * Compose the customer-facing booking confirmation WhatsApp message
 * used by the one-click "Send to WhatsApp" admin action.
 *
 * Populates: customer first name, booking ref, pickup date + time,
 * pickup / drop-off, passenger + luggage count, vehicle, assigned
 * driver (when known) and a Google Maps pin for the pickup.
 */
export function buildBookingConfirmationMessage(booking) {
  if (!booking) return "";
  const name = firstName(booking.name || booking.customer_name);
  const ref = booking.ref || booking.id?.slice(0, 8) || "—";
  const date = booking.pickup_date || "—";
  const time = booking.pickup_time || "—";
  const pickup = booking.pickup_location || "—";
  const isHourly = booking.booking_type === "hourly";
  const dropoff = isHourly
    ? `${booking.hourly_hours || "?"}h charter`
    : (booking.dropoff_location || "—");
  const pax = booking.passengers ?? "?";
  const luggage = booking.luggage;
  const luggageStr = (luggage !== undefined && luggage !== null && luggage !== "")
    ? ` · ${luggage} bag${Number(luggage) === 1 ? "" : "s"}`
    : "";
  const vehicle = booking.vehicle_type || booking.vehicle_category || "Premium vehicle";

  let driverLine = "";
  if (booking.driver_name) {
    driverLine = `\n🧑‍✈️ Your driver: ${booking.driver_name}`;
    if (booking.driver_phone) driverLine += ` (${booking.driver_phone})`;
  }

  const mapsUrl = buildPickupMapsUrl(booking);
  const mapsLine = mapsUrl ? `\n\n📍 Pickup on Google Maps:\n${mapsUrl}` : "";
  const flightLine = booking.flight_number
    ? `\n✈️ Flight: ${booking.flight_number}`
    : "";

  return (
    `Hi ${name}, this is Juke Transfers confirming your ride (Ref ${ref}).\n\n` +
    `📅 ${date} at ${time}\n` +
    `🚏 Pickup: ${pickup}\n` +
    `🎯 Drop-off: ${dropoff}\n` +
    `👥 ${pax} passenger${Number(pax) === 1 ? "" : "s"}${luggageStr}\n` +
    `🚗 Vehicle: ${vehicle}` +
    flightLine +
    driverLine +
    mapsLine +
    `\n\nAny questions, just reply here. Safe travels — the Juke team.`
  );
}

// Sanitise the phone number: keep leading +, digits only.
export function normalisePhone(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  return plus + trimmed.replace(/[^\d]/g, "");
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(
    navigator.userAgent || ""
  );
}

/**
 * Open WhatsApp in a new tab / native app with a pre-filled message.
 * Returns the URL that was opened so callers can copy it if popups are blocked.
 */
export function openWhatsApp(phone, message) {
  const num = normalisePhone(phone).replace(/^\+/, "");
  const text = encodeURIComponent(message || "");
  // Fallback: no number → open universal composer, customer picks contact.
  const target = num
    ? isMobileDevice()
      ? `whatsapp://send?phone=${num}&text=${text}`
      : `https://wa.me/${num}?text=${text}`
    : `https://wa.me/?text=${text}`;
  try {
    window.open(target, "_blank", "noopener,noreferrer");
  } catch {
    window.location.href = target;
  }
  return target;
}
